/**
 * Where things end up: the bench's position-level answers.
 *
 * Two halves, and they are different questions. `reconcile` answers "what else
 * must move so every length still holds" — a solve, over the freedoms the
 * physics gives. The three below answer "which side of the hook does it hang
 * on" — sideways placement, over a coordinate the physics does NOT have. The
 * long comment above them says why that second half is still three functions
 * and not one.
 *
 * ## reconcile
 *
 * The bench has always solved its constraints at ONE level — accelerations,
 * `ΣF = ma` with multipliers, in `solve.ts`. This is the same constraint graph
 * solved one derivative lower: not "what forces satisfy the rows" but "what
 * POSITIONS satisfy them". It is the step that makes a rope behave like a rope
 * while the bench is being built.
 *
 * ## Why it is needed
 *
 * A rope over a sheave keeps its TOTAL length, not the length of either side.
 * Drag one weight of an Atwood down 0.4 m and the left run grows 0.4 m, the
 * right run shrinks 0.4 m, and the rope is untouched — the rope slides, which
 * is the one thing a pulley is for. An edit that may only move the part under
 * the finger cannot express that: the only way it can keep a total fixed while
 * one side grows is to refuse the drag. So before this existed, dragging
 * anything on a taut rope moved it exactly nowhere, and setting a bench up was
 * a fight.
 *
 * ## What it may move, and why that is not what the solver may move
 *
 * Very nearly the same freedoms — a weight travels down its own line, a block
 * slides along the face it rests on, a hook on a beam and a table on the floor
 * do not move at all — with one difference that matters: PINS ARE IGNORED.
 *
 * A pin says "do not fall when I press play". It does not say "do not let the
 * bench be arranged around you", and reading it as the second would disable
 * this on exactly the bench it is for, because during setup everything is
 * pinned. That is not a guess: `setRopeLength` learned it the hard way — its
 * first version read the constraint rows, found none, and quietly did nothing
 * at all on every bench a reader would actually try it on.
 *
 * ## The arithmetic
 *
 * Newton, on rows that already exist. A span's length is a sum of distances
 * around wheels and `lengthRows` in `constraints.ts` is its gradient, so a
 * step is the least-norm solution of
 *
 *     A Δq = −gap        A = ∂c/∂q for the rows currently violated
 *
 * taken as `Δq = Aᵀ(A Aᵀ)⁻¹(−gap)` so that the bench moves as little as it can
 * and still be consistent. Least-norm is the whole reason the result looks
 * right: of all the ways to take up 0.4 m of rope, it picks the one that
 * disturbs the arrangement least.
 *
 * One-sided rows join in when they are VIOLATED or already AT their limit — a
 * slack rope stays slack and a weight off the floor stays off it, but a rope
 * that is exactly taut is held taut while the step is worked out, because
 * otherwise the step is free to stretch it and the next one has to undo that.
 * `leastNorm` below says what that cost and what it buys. A rod is two-sided
 * and is a row whenever it is off its length in either direction.
 */
import { constraintsOf } from './constraints';
import { solveLinear } from './linalg';
import { displaced } from './timeline';
import { Body, Constraint, World } from './types';
import { bodiesOf, isFree, ropesOf, spansOf } from './world';

/** A metre is a long way on this bench; a tenth of a millimetre is not. */
const TOL = 1e-7;

/** Newton can overshoot badly on a nearly-flat run. Nothing moves further than
 *  this in one step, which costs a few more steps and never a wild jump. */
const MOST = 0.5;

/**
 * The bodies as the ARRANGER may move them: the physics freedoms, minus
 * whatever is under the finger, minus whatever the reader has pinned.
 *
 * Pins used to be IGNORED here, and the reason was sound at the time: a part
 * arrived on the bench pinned by default, so honouring pins would have
 * disabled this on every bench a reader could actually build. Parts no longer
 * arrive pinned (`place`), so the default is gone and with it the reason — a
 * pin is now only ever the reader saying "not this one", and there is no
 * reading of that under which the arranger may still slide it.
 */
function movable(w: World, held: Set<string>): Body[] {
    return bodiesOf(w).map(b =>
        (b.partIds.some(id => held.has(id)) ? { ...b, axes: [] } : b));
}

/**
 * Is any line being asked to be shorter than the run it spans?
 *
 * Asked of the GEOMETRY and not of the rows, because `constraintsOf` drops a
 * constraint no movable coordinate appears in — correct for the solver, where
 * a row with no unknowns is not an equation, and exactly backwards here: a
 * rope stretched between two points that cannot move is not a constraint to
 * ignore, it is the definition of a move that cannot happen.
 */
function stretched(w: World): boolean {
    const rigid = new Map(ropesOf(w).map(r => [r.id, !!r.rigid]));
    return spansOf(w).some(s => (rigid.get(s.ropeId)
        ? Math.abs(s.now - s.was) > 1e-6
        : s.now - s.was > 1e-6));
}

/** Rows that say where things may BE. Friction and spin say how hard they are
 *  pushed, which is a question for the other solver. */
const positional = (kind: string): boolean =>
    kind === 'rope' || kind === 'contact' || kind === 'separation';

/** Off its limit by more than a tenth of a micron, in the direction the row
 *  forbids. A two-sided row is wrong either way; a one-sided one only below. */
const violated = (c: Constraint): boolean =>
    (c.oneSided ? -c.gap : Math.abs(c.gap)) > TOL;

/**
 * One step: the least move that fixes the violated rows WITHOUT breaking the
 * ones already at their limit.
 *
 * Those second rows are the whole of this function. A rope that is exactly
 * taut is not "fine, leave it out" — it is a rope that may go slack and may
 * NOT stretch, so a step that lengthens its run is not a step, it is the next
 * violation. Left out, the arithmetic zig-zagged: winding a rope in by a
 * millimetre moved the pulley down half of it, which stretched the taut rope
 * above; the next step moved the pulley back up, which un-shortened the first
 * rope; and forty steps later a third of the millimetre had been taken up and
 * `reconcile` gave up and said no such arrangement exists. It does exist — the
 * two moves just have to be made TOGETHER, which is what putting both rows in
 * one system means.
 *
 * `standing` rows go in as EQUALITIES — hold this length exactly — and that is
 * too strong on its own, because a taut rope is allowed to go slack and a
 * block on the floor is allowed to be lifted off it. So each is released again
 * if its multiplier comes back negative: a one-sided row's force may only push
 * the way the row points (`Constraint`), and a negative multiplier is the
 * arithmetic asking a rope to shove or a floor to pull. Drop those and solve
 * again, which is the active-set loop and terminates because a row released is
 * never taken back in the same step.
 */
function leastNorm(
    drive: Constraint[],
    standing: Constraint[],
    line: (c: Constraint) => number[],
): { A: number[][]; mu: number[] | null } {
    let keep = standing.map(() => true);
    for (let pass = 0; pass <= standing.length; pass++) {
        const set = [...drive, ...standing.filter((_, i) => keep[i])];
        const A = set.map(line);
        const mu = solveLinear(
            A.map(ri => A.map(rj => ri.reduce((sum, v, k) => sum + v * rj[k], 0))),
            set.map(c => -c.gap),
        );
        /* Rows that are not independent make the Gram matrix singular. Holding
         * fewer of them is the way out, and holding none of them is the old
         * behaviour — worse, but not nothing. */
        if (!mu) {
            if (!keep.some(Boolean)) return { A, mu: null };
            keep = keep.map(() => false);
            continue;
        }
        let freed = false;
        let k = drive.length;
        keep = keep.map((on, i) => {
            if (!on) return false;
            const drop = mu[k++] < -1e-12;
            freed = freed || drop;
            return !drop;
        });
        if (!freed) return { A, mu };
    }
    return { A: drive.map(line), mu: null };
}

/**
 * Put the bench somewhere every length is satisfied, without moving `held`.
 *
 * `null` when there is no such place — both ends of a taut rope fixed to the
 * beam, say. That is a real answer and the caller is expected to have one: a
 * drag stops at the last position that worked.
 */
export function reconcile(w: World, held: Set<string>): World | null {
    let at = w;
    /* Ropes this solve has had to take up, and how many steps have gone on
     * finishing the job. A rope row is one-sided, so a step that overshoots
     * leaves it SLACK and nothing then objects — which is how shortening a
     * rope by 40 cm used to hand back a rope 3 cm slack, taut being the one
     * thing the reader had asked for. Once a rope has been pulled in it is
     * driven to its length exactly, for a few steps; the cap is there because
     * a rope that cannot reach taut (something else is in the way) must still
     * come back with the arrangement it did reach. */
    const pulled = new Set<string>();
    let polish = 0;
    for (let step = 0; step < 40; step++) {
        const bodies = movable(at, held);
        const rows = constraintsOf(at, bodies).filter(c => positional(c.kind));
        const bad = rows.filter(violated);
        const loose = polish < 8
            ? rows.filter(c => pulled.has(c.id) && !violated(c) && c.gap > TOL)
            : [];
        if (!bad.length && !loose.length) return stretched(at) ? null : at;
        if (!bad.length) polish++;
        bad.forEach(c => { if (c.kind === 'rope' && c.oneSided) pulled.add(c.id); });

        const idx = new Map<string, number>();
        bodies.filter(isFree).forEach(b => b.axes.forEach((_, i) => idx.set(`${b.id}#${i}`, idx.size)));
        if (!idx.size) return null;

        const line = (c: Constraint): number[] => {
            const row = new Array(idx.size).fill(0) as number[];
            c.rows.forEach(r => {
                const k = idx.get(`${r.coord.bodyId}#${r.coord.axis}`);
                if (k !== undefined) row[k] += r.coefficient;
            });
            return row;
        };
        // A row nothing movable appears in can never be satisfied from here.
        if (bad.map(line).some(row => row.every(v => Math.abs(v) < 1e-12))) return null;

        const standing = rows.filter(c => !violated(c) && c.oneSided && c.gap <= TOL);
        const { A, mu } = leastNorm([...bad, ...loose], standing, line);
        if (!mu) return null;

        const dq: Record<string, number[]> = {};
        let far = 0;
        bodies.filter(isFree).forEach(b => {
            dq[b.id] = b.axes.map((_, i) => {
                const k = idx.get(`${b.id}#${i}`) as number;
                const v = A.reduce((s, row, j) => s + row[k] * mu[j], 0);
                far = Math.max(far, Math.abs(v));
                return v;
            });
        });
        if (!Number.isFinite(far)) return null;
        const scale = far > MOST ? MOST / far : 1;
        if (scale !== 1) Object.keys(dq).forEach(id => { dq[id] = dq[id].map(v => v * scale); });

        at = displaced(at, bodies, dq);
    }
    return null;
}
